// FILE: src/components/ManagerSelect.jsx
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth, db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { setManagerMode, clearAllManagerTraces, setHardblock } from "../lib/managerMode";

const tryParse = (t) => { try { return JSON.parse(t); } catch { return null; } };
async function fetchJSON(url, options) {
  const r = await fetch(url, options);
  const txt = await r.text();
  const j = tryParse(txt) || {};
  return { ok: r.ok, body: j, raw: txt, status: r.status };
}

export default function ManagerSelect({ user }) {
  const nav = useNavigate();
  const loc = useLocation();

  const [authed, setAuthed] = useState(false);
  const [items, setItems] = useState([]);
  const [acceptedMsg, setAcceptedMsg] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    const q = new URLSearchParams(loc.search);
    setAcceptedMsg(q.get("accepted") === "1" || q.get("accepted") === "true");
  }, [loc.search]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setAuthed(!!u));
    return () => unsub();
  }, []);

  useEffect(() => {
    (async () => {
      const u = auth.currentUser;
      if (!u) return;
      setLoading(true);
      setErr("");
      try {
        const tok = await u.getIdToken();
        const api = await fetchJSON("/api/accountManagers/listForManager", {
          headers: { Authorization: `Bearer ${tok}` },
        });
        if (api.ok && Array.isArray(api.body.items)) {
          setItems(api.body.items.map((x) => ({
            id: x.id,
            accountId: x.accountId || x.ownerUid || x.uid || "",
            name: x.name || x.ownerName || x.ownerEmail || "Account",
            accessEnabled: !!x.accessEnabled,
            acceptedAt: x.acceptedAt || null,
            accountContext: x.accountContext || "business",
          })));
          return;
        }

        const email = (u.email || "").toLowerCase();
        const hits = [];

        if (email) {
          const q1 = query(collection(db, "accountManagers"), where("email", "==", email));
          (await getDocs(q1)).forEach((d) => {
            const x = d.data() || {};
            hits.push({
              id: d.id,
              accountId: x.uid || x.ownerUid || "",
              name: x.ownerName || x.ownerEmail || "Account",
              accessEnabled: !!x.accessEnabled,
              acceptedAt: x.acceptedAt || null,
              accountContext: x.accountContext || "business",
            });
          });
        }

        const q2 = query(collection(db, "accountManagers"), where("managerUid", "==", u.uid));
        (await getDocs(q2)).forEach((d) => {
          const x = d.data() || {};
          hits.push({
            id: d.id,
            accountId: x.uid || x.ownerUid || "",
            name: x.ownerName || x.ownerEmail || "Account",
            accessEnabled: !!x.accessEnabled,
            acceptedAt: x.acceptedAt || null,
            accountContext: x.accountContext || "business",
          });
        });

        const byId = new Map();
        for (const h of hits) {
          if (!h.accountId) continue;
          if (!byId.has(h.accountId)) byId.set(h.accountId, h);
        }
        setItems(Array.from(byId.values()));
      } catch (e) {
        setErr(e.message || "Failed to load");
        setItems([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [authed]);

  const isPaidUser = useMemo(() => {
    if (!user) return false;
    if (user.subscriptionStatus === "active") return true;
    if (user.subscription?.status === "active") return true;
    if (user.subscription?.active === true) return true;
    if (user.subscription?.subscriptionStatus === "active") return true;
    if (user.isAdmin) return true;
    return false;
  }, [user]);

  const hasPersonalAccount = useMemo(() => {
    if (!user) return false;
    if (user.personalAccount?.active === true) return true;
    if (user.subscriptionStatus === "active") return true;
    if (user.subscription?.status === "active") return true;
    if (user.subscription?.active === true) return true;
    // A free personal account is still an account. Checking only for a paid
    // subscription left free users with no route back to their own box: the
    // page offered them plan upsells and nothing else.
    if (user.accountTypes?.personal === true) return true;
    if (user.personalAccount && user.personalAccount.plan) return true;
    if (user.planKey) return true;
    return false;
  }, [user]);

  const businessTenantId = useMemo(() => {
    if (!user) return "";
    return user.businessAccount?.tenantId || user.primaryTenantId || user.tenantId || "";
  }, [user]);

  const businessRole = useMemo(() => {
    if (!user) return "";
    return String(user.businessAccount?.role || user.tenantRole || user.role || "").toLowerCase();
  }, [user]);

  const hasBusinessAccount = useMemo(() => {
    return !!businessTenantId && ["owner", "hr_admin", "employee"].includes(businessRole);
  }, [businessTenantId, businessRole]);

  function goPersonalHome() {
    try {
      setHardblock(true);
      clearAllManagerTraces();
      document.body.classList.remove("lwb-readonly");
    } catch {}
    window.location.assign("/");
  }

  function goBusinessHome() {
    try {
      setHardblock(true);
      clearAllManagerTraces();
      document.body.classList.remove("lwb-readonly");
    } catch {}
    if (businessTenantId && ["owner", "hr_admin"].includes(businessRole)) {
      window.location.assign(`/tenant/${businessTenantId}`);
      return;
    }
    window.location.assign("/");
  }

  const personalManagedItems = useMemo(
    () => items.filter((it) => (it.accountContext || "business") === "personal"),
    [items]
  );

  const businessManagedItems = useMemo(
    () => items.filter((it) => (it.accountContext || "business") !== "personal"),
    [items]
  );


  const myAccountCta = useMemo(() => {
    if (!hasPersonalAccount && !hasBusinessAccount) return null;
    return (
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,.06)", padding: "20px 24px", marginBottom: 16 }}>
        <h3 style={{ margin: "0 0 14px", color: "#003353", fontSize: 16 }}>Your account access</h3>
        <div style={{ display: "grid", gap: 10 }}>
          {hasPersonalAccount && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#f6f7fb", borderRadius: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: "#111827", fontSize: 14 }}>My Personal Account</div>
                <div style={{ fontSize: 12, color: "#657899" }}>Open your personal LastWish Box account</div>
              </div>
              <button onClick={goPersonalHome} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#f15822", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                Open
              </button>
            </div>
          )}
          {hasBusinessAccount && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#f6f7fb", borderRadius: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: "#111827", fontSize: 14 }}>My Business Account</div>
                <div style={{ fontSize: 12, color: "#657899" }}>Continue to your business account dashboard</div>
              </div>
              <button onClick={goBusinessHome} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: "#f15822", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                Open
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }, [hasPersonalAccount, hasBusinessAccount, businessTenantId, businessRole]);

  // Routing on login:
  //   own account        -> straight to their own dashboard
  //   no own account     -> this chooser, since managed accounts are all they
  //                         have and there may be several
  // Switch account still brings them back here deliberately, which is what the
  // ref guards: without it, navigating here would bounce them straight out.
  // ?switch=1 means the user clicked Switch account and wants this screen.
  // A ref alone was not enough: navigating here mounts a fresh component with
  // the ref reset, so they were bounced straight back to the dashboard.
  const askedForChooser = new URLSearchParams(loc.search || "").get("switch") === "1";

  const skippedRef = useRef(false);
  useEffect(() => {
    if (loading || skippedRef.current || askedForChooser) return;
    if (!hasPersonalAccount && !hasBusinessAccount) return;
    skippedRef.current = true;
    goPersonalHome();
  }, [loading, hasPersonalAccount, hasBusinessAccount, askedForChooser]);

  async function handleLoginManaged(item, e) {
    e?.preventDefault?.();

    const u = auth.currentUser;
    if (!u) {
      nav(`/login?redirect=${encodeURIComponent("/manager-select")}`);
      return;
    }

    const ownerUid = item.accountId || item.ownerUid || item.uid || "";
    if (!ownerUid) {
      alert("Missing owner id for this managed business account.");
      return;
    }
    if (!item.accessEnabled) {
      alert("Access has not been enabled yet by the business account owner or administrator.");
      return;
    }

    setManagerMode(ownerUid, u.uid, item.id || null);
    window.location.assign("/manager");
  }

  // Render nothing while a skip is pending, otherwise the chooser paints for a
  // moment before the redirect fires and the user sees it flash past.
  const willSkip =
    !askedForChooser && (loading || hasPersonalAccount || hasBusinessAccount);
  if (authed && willSkip) return null;

  if (!authed) {
    return (
      <div style={{ padding: 16 }}>
        <h2>Managed Accounts</h2>
        <p>Please sign in to view any personal or business accounts you can manage.</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f6f7fb", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px" }}>
      <div style={{ width: "min(680px,100%)" }}>

        {/* Header */}
        <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,.06)", padding: "28px 24px", marginBottom: 16, textAlign: "center" }}>
          <img src="/Lastwishbox-logo.png" alt="LastWishBox" style={{ height: 44, objectFit: "contain", marginBottom: 16 }} />
          <h2 style={{ margin: "0 0 8px", color: "#003353", fontSize: 22 }}>Choose your access</h2>
          <p style={{ margin: 0, color: "#657899", fontSize: 15, lineHeight: 1.5 }}>
            Open your personal account, business account, or any managed accounts with read-only access.
          </p>
          {acceptedMsg && (
            <div style={{ background: "#ecfdf5", border: "1px solid #bbf7d0", color: "#065f46", borderRadius: 10, padding: "10px 14px", marginTop: 14, fontSize: 14 }}>
              ✓ Access accepted. If the owner has enabled your access, you can open that managed account below.
            </div>
          )}
        </div>

        {/* Your account access */}
        {myAccountCta}

        {/* Managed Personal Accounts */}
        <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,.06)", padding: "20px 24px", marginBottom: 16 }}>
          <h3 style={{ margin: "0 0 4px", color: "#003353", fontSize: 16 }}>Managed Personal Accounts</h3>
          <p style={{ margin: "0 0 14px", color: "#657899", fontSize: 13 }}>Personal accounts you have permission to view in read-only mode.</p>
          {err && <div style={{ color: "#991b1b", marginBottom: 8, fontSize: 13 }}>{err}</div>}
          {loading && <div style={{ color: "#657899", fontSize: 13 }}>Loading…</div>}
          {!loading && personalManagedItems.length === 0 && (
            <div style={{ color: "#9ca3af", fontSize: 13, fontStyle: "italic" }}>No managed personal accounts available yet.</div>
          )}
          {!loading && personalManagedItems.map((it) => (
            <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#f6f7fb", borderRadius: 10, marginBottom: 8 }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#003353", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                {(it.name || "?")[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: "#111827", fontSize: 14 }}>{it.name || it.ownerEmail || "Personal Account"}</div>
                <div style={{ fontSize: 12, color: it.accessEnabled ? "#059669" : "#9ca3af" }}>
                  {it.accessEnabled ? "✓ Access enabled" : "Awaiting access"}
                </div>
              </div>
              <button
                type="button"
                disabled={!it.accessEnabled}
                onClick={(e) => handleLoginManaged(it, e)}
                title={it.accessEnabled ? "Open managed personal account" : "Owner must enable access first"}
                style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: it.accessEnabled ? "#f15822" : "#e5e7eb", color: it.accessEnabled ? "#fff" : "#9ca3af", fontWeight: 700, fontSize: 13, cursor: it.accessEnabled ? "pointer" : "not-allowed" }}
              >
                Open
              </button>
            </div>
          ))}
        </div>

        {/* Managed Business Accounts */}
        <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,.06)", padding: "20px 24px", marginBottom: 16 }}>
          <h3 style={{ margin: "0 0 4px", color: "#003353", fontSize: 16 }}>Managed Business Accounts</h3>
          <p style={{ margin: "0 0 14px", color: "#657899", fontSize: 13 }}>Business accounts you have permission to view in read-only mode.</p>
          {!loading && businessManagedItems.length === 0 && (
            <div style={{ color: "#9ca3af", fontSize: 13, fontStyle: "italic" }}>No managed business accounts available yet.</div>
          )}
          {!loading && businessManagedItems.map((it) => (
            <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "#f6f7fb", borderRadius: 10, marginBottom: 8 }}>
              <div style={{ width: 38, height: 38, borderRadius: "50%", background: "#980000", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                {(it.name || "?")[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, color: "#111827", fontSize: 14 }}>{it.name || it.ownerEmail || "Business Account"}</div>
                <div style={{ fontSize: 12, color: it.accessEnabled ? "#059669" : "#9ca3af" }}>
                  {it.accessEnabled ? "✓ Access enabled" : "Awaiting access"}
                </div>
              </div>
              <button
                type="button"
                disabled={!it.accessEnabled}
                onClick={(e) => handleLoginManaged(it, e)}
                title={it.accessEnabled ? "Open managed business account" : "Owner or administrator must enable access first"}
                style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: it.accessEnabled ? "#f15822" : "#e5e7eb", color: it.accessEnabled ? "#fff" : "#9ca3af", fontWeight: 700, fontSize: 13, cursor: it.accessEnabled ? "pointer" : "not-allowed" }}
              >
                Open
              </button>
            </div>
          ))}
        </div>

        {/* Need your own account? */}
        {!isPaidUser && (
          <div style={{ background: "#fff", borderRadius: 16, boxShadow: "0 2px 12px rgba(0,0,0,.06)", padding: "20px 24px" }}>
            <h3 style={{ margin: "0 0 4px", color: "#003353", fontSize: 16 }}>Need your own account?</h3>
            <p style={{ margin: "0 0 16px", color: "#657899", fontSize: 13 }}>
              Managed access is read-only. Choose a personal or business plan to store your own data.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 16 }}>
                <div style={{ fontWeight: 700, color: "#003353", marginBottom: 4, fontSize: 14 }}>Personal Plan</div>
                <div style={{ fontSize: 12, color: "#657899", marginBottom: 14, lineHeight: 1.5 }}>Store your wishes, documents and messages. From €49.99/yr.</div>
                <button onClick={() => nav("/personal/plans")} style={{ width: "100%", padding: "9px", borderRadius: 8, border: "none", background: "#f15822", color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                  Personal Plans
                </button>
              </div>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 12, padding: 16 }}>
                <div style={{ fontWeight: 700, color: "#003353", marginBottom: 4, fontSize: 14 }}>Business Plan</div>
                <div style={{ fontSize: 12, color: "#657899", marginBottom: 14, lineHeight: 1.5 }}>Full business dashboard and team management. From €199.99/mo.</div>
                <button onClick={() => nav("/plans")} style={{ width: "100%", padding: "9px", borderRadius: 8, border: "1px solid #003353", background: "#fff", color: "#003353", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
                  Business Plans
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
